import { useMemo, useState } from "react";
import WithAuth from "../../hoc/WithAuth";
import { Collapse, FloatButton, message, Modal } from "antd";
import { AuthContextValue, useAuth } from "../../providers/AuthProvider";
import FarmingStageModal from "../../components/modals/FarmingStageModal";
import {
  useDeleteFarmingStage,
  useGetFarmingStageList,
} from "../../api/farming_stage";
import { PlusOutlined } from "@ant-design/icons";
import { DataTable } from "../../components/ui/data-table";
import { AppButton } from "../../components/ui/AppButton";
import { Icons } from "../../assets/images/icons";
import { Input } from "../../components/ui/input";
import { ExportButton } from "../../components/ui/ExportButton";
import { useTablePrint } from "../../hooks/useTablePrint";
import { FarmingStage } from "../../types/farming-stage";
import { Button } from "../../components/ui/button";

const FarmingStagePage = () => {
  const { user } = useAuth() as AuthContextValue;

  /** State */
  const [searchText, setSearchText] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  /** API */
  const { data: farmingStages, isLoading } = useGetFarmingStageList({
    search: searchText || undefined,
    page: currentPage - 1,
    size: pageSize,
  });
  const { handlePrint } = useTablePrint();

  /** Transform farming stage for table */
  const transformedFarmingStage = useMemo((): FarmingStage[] => {
    if (!farmingStages?.data?.content) return [];
    return farmingStages.data?.content.map((item: FarmingStage) => ({
      id: item.id,
      stageName: item.stageName,
      description: item.description,
    }));
  }, [farmingStages]);

  const [selectedId, setSelectedId] = useState<number>(-1);
  const [open, setOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const { mutateAsync: deleteFarmingStage } = useDeleteFarmingStage();

  const handleCreate = () => {
    setOpen(true);
  };

  const hideModal = () => {
    setOpen(false);
    setSelectedId(-1);
  };

  /** Columns */
  const farmingStageColumns = [
    {
      key: "id",
      title: "#",
      width: 100,
      render: (index: number) => <div className="flex">{index + 1}</div>,
    },
    {
      key: "stageName",
      title: "Stage Name",
      dataIndex: "stageName",
      align: "left" as const,
    },
    {
      key: "description",
      title: "Description",
      dataIndex: "description",
      align: "left" as const,
    },
    {
      key: "actions",
      title: "Actions",
      align: "center" as const,
      render: (id: string, record: FarmingStage) => {
        return (
          <Button
            variant="default"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedId(record?.id);
              setOpen(true);
            }}
          >
            <img
              src={Icons.pencilEdit}
              alt="edit"
              className="h-4 w-4 text-gray-100 mr-1"
            />
            Edit
          </Button>
        );
      },
    },
  ];

  /** Handlers */
  const handlePaginationChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    setCurrentPage(1);
  };

  const onDelete = async () => {
    if (selectedId !== -1) {
      try {
        const result = await deleteFarmingStage(selectedId);
        setDeleteOpen(false);

        if (result && result.data) {
          message.success("Farming-Stage deleted successfully.");
        }
      } catch (err: any) {
        message.error("Operation failed.");
      }
    }
  };

  return (
    <>
      <div
        className="bg-transparent lg:bg-white"
        style={{ borderRadius: 8.5, marginTop: 25 }}
      >
        <div className="flex flex-col sm:flex-row justify-between">
          <div className="relative w-full lg:w-64">
            <Input
              value={searchText}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search farming stage"
              className="pl-10 rounded-2xl w-[308px] h-11"
            />
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
              <img src={Icons.search} alt="search" className="w-6 h-6" />
            </div>
          </div>

          <div className="flex gap-2">
            <AppButton
              customIcon={Icons.printIcon}
              variant="outline"
              onClick={() =>
                handlePrint(
                  farmingStageColumns,
                  transformedFarmingStage,
                  "Farming Stage Report"
                )
              }
              className="bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
            >
              Print
            </AppButton>

            <ExportButton
              columns={farmingStageColumns}
              data={transformedFarmingStage}
              fileName="farmingStage"
              title="Farming Stage Report"
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: farmingStages?.data?.totalElements ?? 0,
              }}
              customIcon={Icons.fileExport}
              className="bg-[#DDDDDD] hover:bg-gray-300"
            />

            {user?.roleName === "ROLE_KFT_SUPER_ADMIN" && (
              <AppButton
                customIcon={Icons.addCircle}
                variant="success"
                onClick={handleCreate}
              >
                Create New
              </AppButton>
            )}
          </div>
        </div>

        <div className="hidden md:block">
          <DataTable<FarmingStage>
            data={transformedFarmingStage}
            columns={farmingStageColumns}
            loading={isLoading}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: farmingStages?.data?.totalElements ?? 0,
              onChange: handlePaginationChange,
            }}
            emptyIcon={<img src={Icons.noDataIcon} alt="No data" />}
            emptyText="No farming stage found"
            rowKey="id"
          />
        </div>
      </div>

      <div
        className="hidden md:flex md:flex-col px-[50px]"
        style={{
          borderRadius: 8.5,
          marginTop: 25,
          paddingTop: 22,
          backgroundColor: "white",
        }}
      >
        <FarmingStageModal
          open={open}
          hideModal={hideModal}
          selectedId={selectedId}
        />

        <Modal
          title={<div style={{ color: "black" }}>Confirmation</div>}
          open={deleteOpen}
          centered
          onOk={onDelete}
          onCancel={() => setDeleteOpen(false)}
          closable
          style={{ color: "black" }}
        >
          Are you sure you want to delete this farming-stage?
        </Modal>
      </div>
      <div className="w-full flex flex-col justify-between md:hidden">
        <div className="text-white text-2xl font-bold mb-8"></div>
        {transformedFarmingStage &&
          transformedFarmingStage.map((item: any) => {
            return (
              <Collapse
                key={item.id}
                accordion
                className="mb-1"
                items={[
                  {
                    key: item.stageName,
                    label: item.stageName,
                    children: item.description,
                  },
                ]}
              />
            );
          })}

        {user?.roleName === "ROLE_KFT_SUPER_ADMIN" && (
          <FloatButton
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
          />
        )}
      </div>
    </>
  );
};

export default WithAuth(FarmingStagePage);
